import { axios } from "@/libraries/axios";
import {
	type AddRemoveMemberCasePayloadSchema,
	CreateCaseInfoPayloadSchema,
	CreateCasePayloadSchema,
	type ExportCasePayloadSchema,
	type GetCaseSyncParamsSchema,
	type MedicalNoteFormSchema,
	type NewCaseLetterFormFieldSchema,
	PatientVisitFormSchema,
	PostAddLocationPayloadSchema,
	type SendCaseLetterFormFieldSchema,
	type UpdateCaseLetterFormFieldSchema,
	UpdateCasePayloadSchema,
	type UpdateMedicalNoteFormSchema,
} from "@/schemas";
import type {
	AssignPatientIntoCasePayloadSchema,
	AssignUnassignTeamPayload,
	BaseCaseDataParams,
	Case,
	CasePatientVisit,
	CaseSyncResponse,
	CreateCaseInfosResponse,
	CreateCaseLetterResponse,
	CreateCaseMedicalNotesResponse,
	CreateCaseResponse,
	GetCaseInfosParams,
	GetCaseInfosResponse,
	GetCaseLetterResponse,
	GetCaseLettersResponse,
	GetCaseMedicalNotesResponse,
	GetCasePatientVisitResponse,
	GetCasePatientVisitsResponse,
	GetCaseResponse,
	GetCasesParams,
	GetCasesResponse,
	GetMedicalNotesParams,
	GetPatientCaseResponse,
	Patient,
	PostAddPatientLocationResponse,
	PostRequestAccessCasePayload,
	UpdateCaseLetterResponse,
	UpdateCaseMedicalNotesResponse,
	UpdateCaseResponse,
} from "@/types";
import type { AxiosRequestConfig } from "axios";
import { isDayjs } from "dayjs";
import omit from "lodash/omit";

class CaseService {
	async cases(params: Partial<GetCasesParams>) {
		try {
			const res = await axios.get<GetCasesResponse>("/api/v1/cases", {
				params: {
					page: params.page || 1,
					limit: params.limit || 25,
					...params,
				},
			});
			return res.data;
		} catch (error) {
			return Promise.reject(error);
		}
	}

	async case(id: number) {
		try {
			const res = await axios.get<GetCaseResponse>(`/api/v1/cases/${id}`);
			return res.data;
		} catch (error) {
			return Promise.reject(error);
		}
	}

	async sync(params: GetCaseSyncParamsSchema) {
		try {
			const res = await axios.get<CaseSyncResponse>("/api/v1/cases/sync", {
				params,
			});
			return res.data;
		} catch (error) {
			return Promise.reject(error);
		}
	}

	async create(payload: CreateCasePayloadSchema) {
		try {
			const parsedPayload = CreateCasePayloadSchema.safeParse(payload);
			if (!parsedPayload.success) throw new Error(parsedPayload.error.message);
			const res = await axios.post<CreateCaseResponse>("/api/v1/cases", {
				case: parsedPayload.data,
			});
			return res.data;
		} catch (error) {
			return Promise.reject(error);
		}
	}

	async update(payload: UpdateCasePayloadSchema) {
		try {
			const parsedPayload = UpdateCasePayloadSchema.safeParse(payload);
			if (!parsedPayload.success) throw new Error(parsedPayload.error.message);
			const { id, ...body } = payload;
			const res = await axios.patch<UpdateCaseResponse>(`/api/v1/cases/${id}`, {
				case: body,
			});
			return res.data;
		} catch (error) {
			return Promise.reject(error);
		}
	}

	async assignPatient(payload: AssignPatientIntoCasePayloadSchema) {
		try {
			const res = await axios.post<GetCaseResponse>(
				`/api/v1/cases/${payload.caseId}/assign/${payload.patientId}`,
			);
			return res.data;
		} catch (error) {
			return Promise.reject(error);
		}
	}
	async findByPatientId(patientIcNumber: Patient["ic_number"]) {
		try {
			const res = await axios.get<GetPatientCaseResponse>(
				"/api/v1/cases/by_patient",
				{
					params: {
						ic_number: patientIcNumber,
					},
				},
			);
			return res.data;
		} catch (error) {
			return Promise.reject(error);
		}
	}

	async addLocation(payload: PostAddLocationPayloadSchema) {
		try {
			const parsedPayload = PostAddLocationPayloadSchema.safeParse(payload);
			if (!parsedPayload.success) throw new Error(parsedPayload.error.message);
			const { caseId, ...location } = parsedPayload.data;
			const res = await axios.post<PostAddPatientLocationResponse>(
				`/api/v1/cases/${caseId}/add_location`,
				{
					location,
				},
			);
			return res.data;
		} catch (error) {
			return Promise.reject(error);
		}
	}

	async patientVisits(params: BaseCaseDataParams) {
		try {
			const res = await axios.get<GetCasePatientVisitsResponse>(
				`/api/v1/cases/${params.caseId}/patient_visits`,
				{
					params: {
						page: params.page || 1,
						limit: params.limit || 25,
					},
				},
			);
			return res.data;
		} catch (error) {
			return Promise.reject(error);
		}
	}

	async patientVisit(patientVisitId: CasePatientVisit["id"]) {
		try {
			const res = await axios.get<GetCasePatientVisitResponse>(
				`/api/v1/cases/${patientVisitId}/patient_visit`,
			);
			return res.data;
		} catch (error) {
			return Promise.reject(error);
		}
	}

	async addPatientVisit(payload: PatientVisitFormSchema) {
		try {
			const parsedPayload = PatientVisitFormSchema.safeParse(payload);
			if (!parsedPayload.success) throw new Error(parsedPayload.error.message);
			const { caseId, ...patient_visit } = parsedPayload.data;
			const res = await axios.post<GetCasePatientVisitResponse>(
				`/api/v1/cases/${caseId}/patient_visits`,
				{
					patient_visit,
				},
			);
			return res.data;
		} catch (error) {
			return Promise.reject(error);
		}
	}

	async updatePatientVisit(payload: PatientVisitFormSchema) {
		try {
			const parsedPayload = PatientVisitFormSchema.safeParse(payload);
			if (!parsedPayload.success) throw new Error(parsedPayload.error.message);
			const { caseId, ...patient_visit } = parsedPayload.data;
			const res = await axios.patch<GetCasePatientVisitResponse>(
				`/api/v1/cases/${caseId}/patient_visits`,
				{
					patient_visit,
				},
			);
			return res.data;
		} catch (error) {
			return Promise.reject(error);
		}
	}

	async caseInfos<T = GetCaseInfosResponse<false>>(params: GetCaseInfosParams) {
		try {
			const { caseId, ...infoParams } = params;
			const res = await axios.get<T>(`/api/v1/case_infos/${caseId}`, {
				params: infoParams,
			});
			return res.data;
		} catch (error) {
			return Promise.reject(error);
		}
	}

	async addCaseInfo(payload: CreateCaseInfoPayloadSchema) {
		try {
			const parsedPayload = CreateCaseInfoPayloadSchema.safeParse(payload);
			if (!parsedPayload.success) throw new Error(parsedPayload.error.message);
			const { caseId, ...case_info } = parsedPayload.data;
			const res = await axios.post<CreateCaseInfosResponse>(
				`/api/v1/case_infos/${caseId}`,
				{
					case_info,
				},
			);
			return res.data;
		} catch (error) {
			return Promise.reject(error);
		}
	}

	async exitCase(caseId: Case["id"]) {
		try {
			const res = await axios.get<GetCaseResponse>(
				`/api/v1/cases/${caseId}/exit`,
			);
			return res.data;
		} catch (error) {
			return Promise.reject(error);
		}
	}

	async closedCases(caseId: Case["id"]) {
		try {
			const res = await axios.get<GetCaseResponse>(
				`/api/v1/cases/${caseId}/closed`,
			);
			return res.data;
		} catch (error) {
			return Promise.reject(error);
		}
	}

	async reopenCase(caseId: Case["id"]) {
		try {
			const res = await axios.get<GetCaseResponse>(
				`/api/v1/cases/${caseId}/reopen`,
			);
			return res.data;
		} catch (error) {
			return Promise.reject(error);
		}
	}

	async addMember(payload: AddRemoveMemberCasePayloadSchema) {
		try {
			const { caseId } = payload;
			const res = await axios.post<GetCaseResponse>(
				`/api/v1/cases/${caseId}/add_members`,
				{
					user_ids: payload.user_ids,
					user_aidbox_ids: payload.user_aidbox_ids,
				},
			);
			return res.data;
		} catch (error) {
			return Promise.reject(error);
		}
	}

	async removeMember(payload: AddRemoveMemberCasePayloadSchema) {
		try {
			const { caseId, ...user_ids } = payload;
			const res = await axios.post<GetCaseResponse>(
				`/api/v1/cases/${caseId}/remove_members`,
				{
					...user_ids,
				},
			);
			return res.data;
		} catch (error) {
			return Promise.reject(error);
		}
	}

	async assignCaseToTeam(payload: AssignUnassignTeamPayload) {
		try {
			const { caseId, teamId } = payload;
			const res = await axios.get<any>(`/api/v1/cases/assign/${teamId}`, {
				params: {
					case_id: caseId,
				},
			});
			return res.data;
		} catch (error) {
			return Promise.reject(error);
		}
	}

	async unassignCaseFromTeam(payload: AssignUnassignTeamPayload) {
		try {
			const { caseId, teamId } = payload;
			const res = await axios.get<any>(`/api/v1/cases/unassign/${teamId}`, {
				params: {
					case_id: caseId,
				},
			});
			return res.data;
		} catch (error) {
			return Promise.reject(error);
		}
	}

	async requestAccess(payload: PostRequestAccessCasePayload) {
		try {
			const { caseId, message } = payload;
			const res = await axios.post<GetCaseResponse>(
				`/api/v1/cases/${caseId}/request_access`,
				{ message },
			);
			return res.data;
		} catch (error) {
			return Promise.reject(error);
		}
	}

	async sendEmail(params: ExportCasePayloadSchema) {
		try {
			const { caseId, ...payload } = params;
			const res = await axios.post(
				`/api/v1/cases/${caseId}/send_email`,
				payload,
			);
			return res.data;
		} catch (error) {
			return Promise.reject(error);
		}
	}

	async medicalNotes(
		param: GetMedicalNotesParams,
		config?: AxiosRequestConfig,
	) {
		try {
			const { caseId, ...params } = param;
			const res = await axios.get<GetCaseMedicalNotesResponse>(
				`/api/v1/cases/${caseId}/medical_notes`,
				{
					...config,
					params,
				},
			);
			return res.data;
		} catch (error) {
			return Promise.reject(error);
		}
	}

	async createMedicalNotes(
		param: MedicalNoteFormSchema,
		config?: AxiosRequestConfig,
	) {
		try {
			const { id, text, status, wound_analysis_id } = param;
			const res = await axios.post<CreateCaseMedicalNotesResponse>(
				`/api/v1/cases/${id}/medical_notes`,
				{
					case_medical_note: {
						text,
						...(status ? { status } : {}),
						...(wound_analysis_id ? { wound_analysis_id } : {}),
					},
				},
				config,
			);
			return res.data;
		} catch (error) {
			return Promise.reject(error);
		}
	}

	async signMedicalNote(
		caseId: number | string,
		id: number | string,
		config?: AxiosRequestConfig,
	) {
		try {
			const res = await axios.patch<CreateCaseMedicalNotesResponse>(
				`/api/v1/cases/${caseId}/medical_notes/${id}/sign`,
				{},
				config,
			);
			return res.data;
		} catch (error) {
			return Promise.reject(error);
		}
	}

	async updateMedicalNotes(
		param: UpdateMedicalNoteFormSchema,
		config?: AxiosRequestConfig,
	) {
		try {
			const { id, text } = param;
			const res = await axios.put<UpdateCaseMedicalNotesResponse>(
				`/api/v1/medical_notes/${id}`,
				{
					case_medical_note: { text },
				},
				config,
			);
			return res.data;
		} catch (error) {
			return Promise.reject(error);
		}
	}

	async removeMedicalNotes(medicalNoteId: number, config?: AxiosRequestConfig) {
		try {
			const res = await axios.delete(
				`/api/v1/medical_notes/${medicalNoteId}`,
				config,
			);
			return res.data;
		} catch (error) {
			return Promise.reject(error);
		}
	}

	async getPdf(caseId: number, config?: AxiosRequestConfig) {
		try {
			const res = await axios.get(`/cases/${caseId}/pdf.pdf`, config);
			return res;
		} catch (error) {
			return Promise.reject(error);
		}
	}

	async letters(caseId: number, config?: AxiosRequestConfig) {
		try {
			const res = await axios.get<GetCaseLettersResponse>(
				`/api/v1/case_letters/cases/${caseId}`,
				config,
			);
			return res.data;
		} catch (error) {
			return Promise.reject(error);
		}
	}

	async letter(id: number, config?: AxiosRequestConfig) {
		try {
			const res = await axios.get<GetCaseLetterResponse>(
				`/api/v1/case_letters/${id}`,
				config,
			);
			return res.data;
		} catch (error) {
			return Promise.reject(error);
		}
	}

	async createLetter(
		payload: NewCaseLetterFormFieldSchema,
		config?: AxiosRequestConfig,
	) {
		try {
			const bodyPayload = payload;
			if (
				bodyPayload.case_letter.date &&
				isDayjs(bodyPayload.case_letter.date)
			) {
				// @ts-expect-error
				bodyPayload.case_letter.date =
					bodyPayload.case_letter.date.format("DD-MM-YYYY");
			}
			if (
				bodyPayload.case_letter.time &&
				isDayjs(bodyPayload.case_letter.time)
			) {
				// @ts-expect-error
				bodyPayload.case_letter.time =
					bodyPayload.case_letter.time.format("hh:mm A");
			}
			const res = await axios.post<CreateCaseLetterResponse>(
				`/api/v1/case_letters/cases/${payload.caseId}`,
				omit(bodyPayload, ["caseId"]),
				config,
			);
			return res.data;
		} catch (error) {
			return Promise.reject(error);
		}
	}

	async sendLetter(
		payload: SendCaseLetterFormFieldSchema,
		config?: AxiosRequestConfig,
	) {
		try {
			if (payload.emails) {
				// @ts-expect-error
				payload.emails = payload.emails.split(/\s*,\s*/).filter(Boolean);
			}
			const res = await axios.post(
				`/api/v1/case_letters/${payload.id}/send_email`,
				omit(payload, ["id"]),
				config,
			);
			return res.data;
		} catch (error) {
			return Promise.reject(error);
		}
	}

	async updateLetter(
		payload: UpdateCaseLetterFormFieldSchema,
		config?: AxiosRequestConfig,
	) {
		try {
			const bodyPayload = payload;
			if (
				bodyPayload.case_letter.date &&
				isDayjs(bodyPayload.case_letter.date)
			) {
				// @ts-expect-error
				bodyPayload.case_letter.date =
					bodyPayload.case_letter.date.format("DD-MM-YYYY");
			}
			if (
				bodyPayload.case_letter.time &&
				isDayjs(bodyPayload.case_letter.time)
			) {
				// @ts-expect-error
				bodyPayload.case_letter.time =
					bodyPayload.case_letter.time.format("hh:mm A");
			}
			const res = await axios.patch<UpdateCaseLetterResponse>(
				`/api/v1/case_letters/${payload.id}`,
				omit(bodyPayload, ["id"]),
				config,
			);
			return res.data;
		} catch (error) {
			return Promise.reject(error);
		}
	}

	async deleteLetter(caseId: number, config?: AxiosRequestConfig) {
		try {
			const res = await axios.delete(`/api/v1/case_letters/${caseId}`, config);
			return res.data;
		} catch (error) {
			return Promise.reject(error);
		}
	}

	async getLetterPdf(id: number, config?: AxiosRequestConfig) {
		try {
			const res = await axios.get(`/case_letters/${id}/pdf.pdf`, config);
			return res;
		} catch (error) {
			return Promise.reject(error);
		}
	}
}

export const caseService = Object.freeze(new CaseService());
